import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import {
  getAsistenciasLaborales,
  getAsistenciasLaboralesStats,
  crearAsistenciaLaboralManual,
  eliminarAsistenciaLaboral,
} from '../../../services/asistenciaLaboral.service';

import { agruparAsistenciasPorJornada, formatearFechaVisual } from '../utils/asistenciaLaboralUtils';

export const useAsistenciaLaboral = () => {
  const todayStr = dayjs().format('YYYY-MM-DD');

  const [asistencias, setAsistencias] = useState([]);
  const [stats, setStats] = useState({
    fecha: todayStr,
    total_registros: 0,
    total_trabajadores: 0,
    total_entradas: 0,
    total_salidas: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    fecha: todayStr,
    fecha_desde: '',
    fecha_hasta: '',
    tipo_registro: 'all',
    search: '',
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchAsistencias = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const currentFilters = filtersRef.current;
      const params = {};
      if (currentFilters.fecha) params.fecha = currentFilters.fecha;
      if (currentFilters.fecha_desde) params.fecha_desde = currentFilters.fecha_desde;
      if (currentFilters.fecha_hasta) params.fecha_hasta = currentFilters.fecha_hasta;
      if (currentFilters.tipo_registro && currentFilters.tipo_registro !== 'all') {
        params.tipo_registro = currentFilters.tipo_registro;
      }
      if (currentFilters.search) params.search = currentFilters.search;

      const data = await getAsistenciasLaborales(params);
      setAsistencias(data);
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Error al cargar asistencias laborales');
      } else {
        console.error('Error en polling de asistencias:', err);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const fetchStats = useCallback(async (targetDate, silent = false) => {
    if (!silent) setLoadingStats(true);
    try {
      const dateToFetch = targetDate || filtersRef.current.fecha || todayStr;
      const data = await getAsistenciasLaboralesStats(dateToFetch);
      setStats(data);
    } catch (err) {
      if (!silent) console.error('Error al cargar stats:', err);
    } finally {
      if (!silent) setLoadingStats(false);
    }
  }, [todayStr]);

  // Carga inicial y ante cambio de filtros
  useEffect(() => {
    fetchAsistencias(false);
    fetchStats(null, false);
  }, [filters, fetchAsistencias, fetchStats]);

  // Polling automático en tiempo real cada 4 segundos (silencioso sin interrumpir UI)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAsistencias(true);
      fetchStats(null, true);
    }, 4000);

    return () => clearInterval(interval);
  }, [fetchAsistencias, fetchStats]);

  const handleCreateManual = async (payload) => {
    const nuevo = await crearAsistenciaLaboralManual(payload);
    await fetchAsistencias(false);
    await fetchStats(null, false);
    return nuevo;
  };

  const handleDelete = async (id) => {
    await eliminarAsistenciaLaboral(id);
    await fetchAsistencias(false);
    await fetchStats(null, false);
  };

  const handleDeleteMultiple = async (ids = []) => {
    for (const id of ids) {
      if (id) {
        await eliminarAsistenciaLaboral(id);
      }
    }
    await fetchAsistencias(false);
    await fetchStats(null, false);
  };

  const exportToExcel = async (jornadasCustom = null) => {
    const dataAExportar = jornadasCustom || agruparAsistenciasPorJornada(asistencias);

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'InscribCórdoba - Asistencia Laboral';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet('Jornadas Laborales');

    // Título Principal
    worksheet.mergeCells('A1:I1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'GOBIERNO DE LA PROVINCIA DE CÓRDOBA — REPORTE CONSOLIDADO DE JORNADAS LABORALES';
    titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF004582' }, // CBA DarkBlue
    };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 35;

    // Subtítulo con fecha de emisión
    worksheet.mergeCells('A2:I2');
    const subTitleCell = worksheet.getCell('A2');
    subTitleCell.value = `Filtro aplicado: ${filters.fecha ? `Fecha: ${filters.fecha}` : filters.fecha_desde && filters.fecha_hasta ? `Desde ${filters.fecha_desde} hasta ${filters.fecha_hasta}` : 'Historial General'} | Total jornadas: ${dataAExportar.length} | Generado: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`;
    subTitleCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF333333' } };
    subTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(2).height = 20;

    // Fila en blanco
    worksheet.addRow([]);

    // Cabeceras de tabla
    const headers = [
      'Fecha',
      'CUIL',
      'Apellido y Nombre',
      'Entrada',
      'Medio Entrada',
      'Salida',
      'Medio Salida',
      'Total Horas',
      'Estado de Jornada',
    ];
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;
    headerRow.eachCell((cell) => {
      cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF009ADA' }, // CBA Blue
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
        bottom: { style: 'medium', color: { argb: 'FF004582' } },
      };
    });

    // Filas de datos agrupados por jornada
    dataAExportar.forEach((jornada) => {
      const horaEntrada = jornada.entrada?.hora ? jornada.entrada.hora.substring(0, 5) + ' hs' : 'Sin registrar';
      const origenEntrada = jornada.entrada?.origen || '-';
      const horaSalida = jornada.salida?.hora
        ? jornada.salida.hora.substring(0, 5) + ' hs'
        : jornada.estado === 'EN_CURSO'
        ? 'Pendiente'
        : jornada.estado === 'SIN_SALIDA'
        ? 'Sin salida'
        : '-';
      const origenSalida = jornada.salida?.origen || '-';
      const estadoVisual =
        jornada.estado === 'COMPLETO'
          ? 'Completa'
          : jornada.estado === 'EN_CURSO'
          ? 'En curso'
          : jornada.estado === 'SIN_SALIDA'
          ? 'Sin salida'
          : 'Inconsistente';

      const row = worksheet.addRow([
        formatearFechaVisual(jornada.fecha),
        jornada.cuil || '',
        jornada.nombre_completo || '-',
        horaEntrada,
        origenEntrada,
        horaSalida,
        origenSalida,
        jornada.totalHorasTexto || '—',
        estadoVisual,
      ]);

      row.height = 20;

      // Alineaciones
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(5).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(6).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(7).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(8).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };

      // Formato condicional de color
      if (jornada.estado === 'COMPLETO') {
        row.getCell(8).font = { color: { argb: 'FF047857' }, bold: true };
        row.getCell(9).font = { color: { argb: 'FF047857' }, bold: true };
      } else if (jornada.estado === 'EN_CURSO') {
        row.getCell(6).font = { color: { argb: 'FFD97706' }, italic: true, bold: true };
        row.getCell(8).font = { color: { argb: 'FF2563EB' }, italic: true, bold: true };
        row.getCell(9).font = { color: { argb: 'FF2563EB' }, bold: true };
      } else if (jornada.estado === 'SIN_SALIDA') {
        row.getCell(6).font = { color: { argb: 'FFDC2626' }, bold: true };
        row.getCell(9).font = { color: { argb: 'FFDC2626' }, bold: true };
      }
    });

    // Auto-ajustar ancho de columnas
    worksheet.columns = [
      { width: 14 }, // Fecha
      { width: 16 }, // CUIL
      { width: 34 }, // Apellido y Nombre
      { width: 14 }, // Entrada
      { width: 14 }, // Medio Entrada
      { width: 14 }, // Salida
      { width: 14 }, // Medio Salida
      { width: 18 }, // Total Horas
      { width: 18 }, // Estado de Jornada
    ];

    // Descargar archivo Excel en el navegador
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Jornadas_Laborales_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return {
    asistencias,
    stats,
    loading,
    loadingStats,
    error,
    filters,
    setFilters,
    fetchAsistencias,
    fetchStats,
    handleCreateManual,
    handleDelete,
    handleDeleteMultiple,
    exportToExcel,
  };
};

export default useAsistenciaLaboral;
